// Sticky non-dismissible banner shown while an Argo employee is acting
// as a customer (ADR-008 §6). The server treats the impersonation
// token as authoritative — this banner is the customer-trust signal
// and the "Exit" affordance.

import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { CAPTURE_EXCLUDE_ATTR } from './feedback/captureScreenshot.js';
import { extractApiError } from '../api/client.js';
import {
  acquireEmployeeTokenFromImpersonation,
  getEmployeeAccessToken,
} from '../api/impersonation.js';

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

export function ImpersonationBanner(): JSX.Element | null {
  const auth = useAuth();
  const navigate = useNavigate();
  const [ending, setEnding] = useState(false);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const active = auth.status === 'signed_in' && Boolean(auth.impersonation);

  // Publish the banner's live height as --sticky-banner-height so every
  // programmatic scrollIntoView() target can clear it (.scroll-target in
  // styles.css). Without it, "Add item" scrolls the new project to the top of
  // the viewport and the banner covers its heading. Measured rather than
  // hardcoded: the banner grows a second line when openJobs() reports an error.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!active || !el) {
      root.style.setProperty('--sticky-banner-height', '0px');
      return;
    }
    const publish = (): void => {
      root.style.setProperty(
        '--sticky-banner-height',
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty('--sticky-banner-height', '0px');
    };
  }, [active]);

  if (auth.status !== 'signed_in' || !auth.impersonation) return null;

  const imp = auth.impersonation;

  // Jump to the employee jobs grid (/employee/jobs) to find an unattributed
  // quote and file it under the right company/contact. That page is a direct
  // employee action guarded by the employee JWT. It's already in memory for a portal-initiated
  // impersonation (Path B), but a Nexus-initiated grant (Path A) never planted
  // one — so when it's missing we mint one FROM the active impersonation
  // session (the rep is already a proven employee; no re-login).
  function openJobs(): void {
    if (getEmployeeAccessToken()) {
      navigate('/employee/jobs');
      return;
    }
    setOpening(true);
    setOpenError(null);
    void (async () => {
      try {
        await acquireEmployeeTokenFromImpersonation();
        navigate('/employee/jobs');
      } catch (err) {
        // 401/403 means the impersonation session is gone or not permitted —
        // re-establish via employee sign-in. Anything else (429, 5xx) is
        // transient: stay put and surface it rather than a misleading login.
        const status = httpStatus(err);
        if (status === 401 || status === 403) {
          navigate('/employee/login');
        } else {
          setOpenError(
            extractApiError(err)?.message ?? 'Could not open the jobs view. Please try again.'
          );
        }
      } finally {
        setOpening(false);
      }
    })();
  }

  return (
    <>
      <div
        ref={bannerRef}
        role="status"
        {...{ [CAPTURE_EXCLUDE_ATTR]: '' }}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#7a1f1f',
          color: 'white',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          fontSize: 14,
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        }}
      >
        <div>
          <strong>Impersonating</strong> {auth.user.email} as <strong>{imp.employee_email}</strong>
          <span style={{ opacity: 0.7, marginLeft: 12 }}>
            (started {new Date(imp.started_at).toLocaleTimeString()})
          </span>
          {openError && (
            <div role="alert" style={{ marginTop: 4, color: '#ffd9d9', fontWeight: 600 }}>
              {openError}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={openJobs}
            disabled={opening}
            title="Find unattributed jobs and file them under the right company and contact"
            style={{
              background: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.7)',
              borderRadius: 4,
              padding: '6px 14px',
              fontWeight: 600,
              cursor: opening ? 'wait' : 'pointer',
            }}
          >
            {opening ? 'Opening…' : 'Unassigned jobs'}
          </button>
          <button
            type="button"
            disabled={ending}
            onClick={() => {
              setEnding(true);
              void (async () => {
                try {
                  await auth.endImpersonation();
                } finally {
                  setEnding(false);
                  navigate('/employee/login', { replace: true });
                }
              })();
            }}
            style={{
              background: 'white',
              color: '#7a1f1f',
              border: 0,
              borderRadius: 4,
              padding: '6px 14px',
              fontWeight: 600,
              cursor: ending ? 'wait' : 'pointer',
            }}
          >
            {ending ? 'Ending…' : 'End impersonation'}
          </button>
        </div>
      </div>
    </>
  );
}
